import {
  AiLesson,
  AiLessonReplayResult,
  AiLessonReplayRun,
  AiLessonReplayToolMode,
  AiLessonReplayVerdict,
  EAiLessonStatus
} from "../aiAgents.types";
import {
  AI_LESSON_REPLAY,
  AI_LESSON_REPLAY_APPROXIMATE_REASONS,
  AI_LESSON_REPLAY_ERROR_CODES,
  AI_LESSON_REPLAY_OUTCOMES,
  AI_LESSON_REPLAY_TOOL_MODES,
  AI_LESSON_REPLAY_VISUAL_TOOLS
} from "../constants/lessons";

// Pure replay helpers (no React, no API client), so they can be unit tested.

const CONFLICT = 409;
// LessonReplayInProgress: its message is the backend sentence (= i18n key).
const REPLAY_IN_PROGRESS_MESSAGE =
  "A replay of this lesson is already running. Wait for it to finish.";
const REPLAY_IN_PROGRESS_EXCEPTION = /ReplayInProgress/;

const includes = <T extends string>(list: readonly T[], value: unknown) =>
  typeof value === "string" && (list as readonly string[]).includes(value);

// A replay needs the permission, a lesson that is not archived, and the turn
// it was corrected from.
export const lessonCanReplay = (
  lesson: Pick<AiLesson, "status" | "sourceTurnMessageId">,
  canManage: boolean
): boolean =>
  canManage &&
  lesson.status !== EAiLessonStatus.Archived &&
  Boolean(lesson.sourceTurnMessageId);

export type ReplayState = "none" | "running" | "interrupted" | "done" | "error";

// "running" only lasts RUNNING_STALE_MS: the server writes the marker before
// the runs, so a deploy or a crash during them leaves it behind.
export const replayState = (
  result: Pick<AiLessonReplayResult, "status" | "startedAt"> | null | undefined,
  nowMs: number
): ReplayState => {
  if (!result) return "none";
  switch (result.status) {
    case "running": {
      const startedMs = Date.parse(result.startedAt);
      return Number.isFinite(startedMs) &&
        nowMs - startedMs < AI_LESSON_REPLAY.RUNNING_STALE_MS
        ? "running"
        : "interrupted";
    }
    case "done":
      return "done";
    case "error":
      return "error";
    default:
      return "none";
  }
};

// The guidance or the triggers were edited after this replay (the version was
// bumped), so its result no longer says anything about the lesson.
export const replayIsOutdated = (
  lesson: Pick<AiLesson, "version" | "lastReplayResult">
): boolean =>
  Boolean(lesson.lastReplayResult) &&
  lesson.lastReplayResult!.lessonVersion !== lesson.version;

// Same refusals as the backend's decideReplayVerdict ("no_result",
// "not_done", "outdated"), plus the permission and the runs (the list route
// leaves them out).
export const replayCanJudge = (
  lesson: Pick<AiLesson, "status" | "version" | "lastReplayResult">,
  canManage: boolean,
  nowMs: number
): boolean => {
  const result = lesson.lastReplayResult;
  return (
    canManage &&
    lesson.status !== EAiLessonStatus.Archived &&
    replayState(result, nowMs) === "done" &&
    !replayIsOutdated(lesson) &&
    Boolean(result && !result.runsOmitted && result.runs.length > 0)
  );
};

// The toast after a verdict. A live lesson keeps its status (the backend only
// moves draft <-> tested), so "it can now be activated" would be wrong there.
export const replayVerdictToastKey = (
  verdict: AiLessonReplayVerdict,
  status: EAiLessonStatus | null | undefined
): string => {
  if (verdict === "failed") return "aiLesson.toast.verdictFailed";
  return status === EAiLessonStatus.Active
    ? "aiLesson.toast.verdictPassedActive"
    : "aiLesson.toast.verdictPassed";
};

const round4 = (value: number) => Math.round(value * 10_000) / 10_000;

// Before launch: each replay is a pair of runs (with the lesson and the
// control). "expected" uses the measured average, "max" the loop's limit.
export const replayEstimateUsd = (
  runs: number
): { expected: number; max: number } => {
  const { EXPECTED_ROUNDS_PER_RUN, MAX_ROUNDS_PER_RUN, COST_USD_PER_ROUND } =
    AI_LESSON_REPLAY;
  const pairs = Math.max(0, runs) * 2;
  return {
    expected: round4(pairs * EXPECTED_ROUNDS_PER_RUN * COST_USD_PER_ROUND),
    max: round4(pairs * MAX_ROUNDS_PER_RUN * COST_USD_PER_ROUND)
  };
};

export const formatUsd = (value: number, locale?: string): string => {
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    currency: "USD",
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits: 3
  };
  try {
    return new Intl.NumberFormat(locale, options).format(value);
  } catch {
    // An unknown locale tag: the browser's own.
    return new Intl.NumberFormat(undefined, options).format(value);
  }
};

// One line per known reason, in the server's order, without repeats.
export const replayApproximateReasonKeys = (
  result: Pick<AiLessonReplayResult, "approximateReasons"> | null | undefined
): string[] => [
  ...new Set(
    (result?.approximateReasons ?? [])
      .filter((reason) =>
        includes(AI_LESSON_REPLAY_APPROXIMATE_REASONS, reason)
      )
      .map((reason) => `aiReplay.reason.${reason}`)
  )
];

// What the run sent besides text (cards, pages, videos), known tools only.
export const replayVisualLabelKeys = (
  run: Pick<AiLessonReplayRun, "visualTools">
): string[] => [
  ...new Set(
    (run.visualTools ?? [])
      .filter((tool) => includes(AI_LESSON_REPLAY_VISUAL_TOOLS, tool))
      .map((tool) => `aiReplay.visual.${tool}`)
  )
];

// null is "nothing recorded" (the real reply has no metric row); an unknown
// value is shown as it is.
export const replayOutcomeKey = (outcome: string | null | undefined): string =>
  outcome == null
    ? "aiTurn.noMetric"
    : includes(AI_LESSON_REPLAY_OUTCOMES, outcome)
      ? `aiReplay.outcome.${outcome}`
      : outcome;

export const replayErrorKey = (code: string | null | undefined): string =>
  includes(AI_LESSON_REPLAY_ERROR_CODES, code)
    ? `aiReplay.error.${code}`
    : "aiReplay.error.unexpected";

// The backend defaults a missing mode to "simulated"; so does the label.
export const replayToolMode = (mode: unknown): AiLessonReplayToolMode =>
  includes(AI_LESSON_REPLAY_TOOL_MODES, mode)
    ? (mode as AiLessonReplayToolMode)
    : "simulated";

interface ErrorLike {
  response?: {
    status?: number;
    data?: { exception?: unknown; message?: unknown } | null;
  };
}

// R1 refused because another replay of this lesson is still running: the
// window then waits for that one's result instead of showing an error.
export const isReplayInProgressError = (error: unknown): boolean => {
  if (error === null || typeof error !== "object") return false;
  const response = (error as ErrorLike).response;
  if (response?.status !== CONFLICT) return false;
  const exception = response.data?.exception;
  const message = response.data?.message;
  return (
    (typeof exception === "string" &&
      REPLAY_IN_PROGRESS_EXCEPTION.test(exception)) ||
    message === REPLAY_IN_PROGRESS_MESSAGE ||
    (Array.isArray(message) && message.includes(REPLAY_IN_PROGRESS_MESSAGE))
  );
};
